import React, { useState } from 'react';
import { useMainAllInformations } from '@/hooks/main';
import { formatJapaneseDate } from '@/utils';
import type { Information } from '@app-types/information';
import { useAuth } from '@shared/providers/AuthContext';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import {
  AppAlert,
  AppButton,
  AppCard,
  AppDivider,
  AppSkeleton,
  AppModal,
  AppTypography,
} from '@/components/atoms';

const nl2br = (text: string): string => text.replace(/\n/g, '<br>');

const HtmlContent: React.FC<{ html?: string | null; className?: string }> = ({
  html,
  className,
}) => {
  return <div className={className} dangerouslySetInnerHTML={{ __html: nl2br(html || '') }} />;
};

/**
 * Information List Component
 *
 * Displays お知らせ (announcements) matching RA2 design, using Ant Design components.
 */
const InformationList: React.FC = () => {
  const { colors } = useTheme();
  const { clinic } = useAuth();
  const [selectedInformation, setSelectedInformation] = useState<Information | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);

  const { data, isLoading, error } = useMainAllInformations(
    clinic?.site_type || '',
    clinic?.clinic_number || ''
  );

  const currentInformations = data?.current ?? [];
  const pastInformations = data?.past ?? [];

  const handlePastClick = (info: Information) => {
    setSelectedInformation(info);
    setIsModalVisible(true);
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    setSelectedInformation(null);
  };

  if (isLoading) {
    return (
      <div className="w-full space-y-6">
        <AppCard>
          <AppSkeleton active paragraph={{ rows: 2 }} />
        </AppCard>
        <AppCard>
          <AppSkeleton active paragraph={{ rows: 3 }} />
        </AppCard>
      </div>
    );
  }

  if (error) {
    return (
      <AppAlert type="error" showIcon description="情報の読み込み中にエラーが発生しました。" />
    );
  }

  return (
    <div className="w-full space-y-6">
      <AppCard
        title="お知らせ"
        className="rounded shadow"
        styles={{
          header: {
            backgroundColor: colors.sectionHeader,
            color: colors.sectionHeaderText,
            padding: '0.75rem 1.25rem',
          },
          body: {
            padding: '1.25rem',
          },
        }}
      >
        {currentInformations.length === 0 ? (
          <AppTypography.Text>お知らせはありません。</AppTypography.Text>
        ) : (
          <div>
            {currentInformations.map((info, index) => (
              <div key={info.id} className="py-3">
                <AppTypography.H5 className="!m-0 !text-[16pt]">
                  <span dangerouslySetInnerHTML={{ __html: info.title }} />
                  <AppTypography.Text type="secondary" className="ml-2 !text-[11pt] font-normal">
                    ({formatJapaneseDate(info.information_date)})
                  </AppTypography.Text>
                </AppTypography.H5>
                <HtmlContent
                  className="mt-2 text-[11pt] whitespace-pre-line break-words overflow-hidden"
                  html={info.contents}
                />
                {index < currentInformations.length - 1 && <AppDivider className="my-2" />}
              </div>
            ))}
          </div>
        )}
      </AppCard>

      <AppCard
        title="過去のお知らせ"
        className="rounded shadow"
        styles={{
          header: {
            backgroundColor: colors.sectionHeader,
            color: colors.sectionHeaderText,
            padding: '0.75rem 1.25rem',
          },
          body: {
            padding: '1.25rem',
          },
        }}
      >
        {pastInformations.length === 0 ? (
          <AppTypography.Text>過去のお知らせはありません。</AppTypography.Text>
        ) : (
          <div>
            {pastInformations.map((info, index) => (
              <div key={info.id} className="py-2">
                <AppTypography.Text className="mr-4">
                  {formatJapaneseDate(info.information_date)}
                </AppTypography.Text>
                <AppTypography.Link
                  className="hover:!underline break-words overflow-hidden"
                  style={{ color: colors.primary }}
                  onClick={() => handlePastClick(info)}
                  dangerouslySetInnerHTML={{ __html: info.title }}
                />
                {index < pastInformations.length - 1 && <AppDivider className="my-2" />}
              </div>
            ))}
          </div>
        )}
      </AppCard>

      <AppModal
        title="過去のお知らせ"
        isOpen={isModalVisible}
        onClose={handleCloseModal}
        maxWidth={766}
        footer={() => (
          <AppButton
            onClick={handleCloseModal}
            icon={<i className="fa fa-close" aria-hidden="true" />}
            style={{
              borderColor: colors.primaryDark,
              color: colors.primaryDark,
            }}
            className="hover:!bg-gray-100 hover:!border-primaryDark hover:!text-primaryDark transition-colors"
          >
            閉じる
          </AppButton>
        )}
        styles={{
          body: {
            height: '500px',
            overflowY: 'auto',
          },
        }}
      >
        {selectedInformation && (
          <div className="w-full space-y-3">
            <AppTypography.H4 className="!m-0 !text-[16pt]">
              <span dangerouslySetInnerHTML={{ __html: selectedInformation.title }} />
              {' '}
              <AppTypography.Text
                type="secondary"
                className="!text-[11pt] font-normal whitespace-nowrap"
              >
                ({formatJapaneseDate(selectedInformation.information_date)})
              </AppTypography.Text>
            </AppTypography.H4>
            <HtmlContent
              className="text-[11pt] whitespace-pre-line"
              html={selectedInformation.contents}
            />
          </div>
        )}
      </AppModal>
    </div>
  );
};

export default InformationList;
